import { axiosClient } from '../../../library/Service/axiosClient';
import PageConfigAiSuggested from './PageConfigAiSuggested.json';

class AiSuggestedService {
    static async pageLoader(params: any): Promise<any> {
        const state: any = {};
        const searchCriteria = this.getDefaultSearchCriteria();

        try {
            const [formconfig, providerResponse, filterDomains] = await Promise.all([
                this.getPageConfig(),
                this.search(searchCriteria),
                this.getDynamicFilterDomains(),
            ]);

            const payload = providerResponse?.data ?? {};
            state.formConfig = formconfig;
            state.data = {
                providerList: payload?.data ?? [],
                pagination: {
                    total: payload?.total ?? 0,
                    page: payload?.page ?? searchCriteria.page,
                    limit: payload?.limit ?? searchCriteria.limit,
                },
                hasMore:
                    (payload?.page ?? searchCriteria.page) * (payload?.limit ?? searchCriteria.limit) <
                    (payload?.total ?? 0),
                searchCriteria,
                filterDomains,
            };
            state.routeInfo = params;
            state.internal = { gridState: {} };
            return state;
        } catch (error) {
            console.error('Error loading AI Suggested Directory:', error);
            return {
                formConfig: PageConfigAiSuggested,
                data: {
                    providerList: [],
                    pagination: { total: 0, page: 1, limit: 20 },
                    hasMore: false,
                    searchCriteria,
                    filterDomains: {},
                },
                routeInfo: params,
                internal: { gridState: {} },
            };
        }
    }

    static getPageConfig() {
        return Promise.resolve(PageConfigAiSuggested);
    }

    static async getDynamicFilterDomains() {
        try {
            const response = await axiosClient().get(
                `${process.env.REACT_APP_REPORTS_SERVER}/ai-suggestions/filters`
            );
            return response?.data || {};
        } catch (error) {
            console.error('Failed to fetch AI suggestion filter domains:', error);
            return {};
        }
    }

    static getDefaultSearchCriteria(): any {
        return {
            fuzzySearchText: '',
            institutes: null,
            category: [],
            countryCode: '',
            cityCode: '',
            confidenceRange: [],
            page: 1,
            limit: 20,
            status: 'Pending',
        };
    }

    static sanitizeCriteria(searchCriteria: any) {
        const criteria: any = {};

        if (searchCriteria.institutes !== null && searchCriteria.institutes !== undefined && searchCriteria.institutes !== '') {
            criteria.institutes = Array.isArray(searchCriteria.institutes)
                ? searchCriteria.institutes
                : [searchCriteria.institutes];
        }

        if (searchCriteria.category && Array.isArray(searchCriteria.category)) {
            criteria.category = searchCriteria.category
                .map((item: any) => (typeof item === 'object' ? Object.values(item)[0] : item))
                .filter(Boolean);
        }

        if (searchCriteria.countryCode) criteria.countryCode = searchCriteria.countryCode;
        if (searchCriteria.cityCode) criteria.cityCode = searchCriteria.cityCode;

        // Confidence range — supports multi-select (array of codes)
        if (searchCriteria.confidenceRange) {
            if (Array.isArray(searchCriteria.confidenceRange)) {
                const codes = searchCriteria.confidenceRange
                    .map((item: any) => (typeof item === 'object' ? Object.values(item)[0] : item))
                    .filter(Boolean);
                if (codes.length > 0) criteria.confidenceRange = codes;
            } else {
                const v = typeof searchCriteria.confidenceRange === 'object'
                    ? Object.values(searchCriteria.confidenceRange)[0]
                    : searchCriteria.confidenceRange;
                if (v) criteria.confidenceRange = v;
            }
        }

        if (searchCriteria.page) criteria.page = searchCriteria.page;
        if (searchCriteria.limit) criteria.limit = searchCriteria.limit;
        if (searchCriteria.status) criteria.status = searchCriteria.status;

        if (searchCriteria.fuzzySearchText !== undefined && searchCriteria.fuzzySearchText !== null) {
            criteria.fuzzySearchText = String(searchCriteria.fuzzySearchText).trim();
        }

        return criteria;
    }

    static search(searchCriteria: any) {
        const criteria = this.sanitizeCriteria(searchCriteria);
        return axiosClient().post(
            `${process.env.REACT_APP_REPORTS_SERVER}/ai-suggestions/search`,
            criteria
        );
    }

    static rejectSuggestion(suggestionId: string) {
        return axiosClient().post(
            `${process.env.REACT_APP_COMMON_API}/providers/ai-suggested/${suggestionId}/reject`,
            {}
        );
    }

    static approveSuggestion(suggestionId: string, payload: any) {
        return axiosClient().post(
            `${process.env.REACT_APP_COMMON_API}/providers/ai-suggested/${suggestionId}/approve`,
            payload
        );
    }
}

export default AiSuggestedService;
